import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BalanceSheetTab = ({ getAuthHeader, asOfDate }) => {
  const [loading, setLoading] = useState(false);
  const [balanceSheet, setBalanceSheet] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const fetchBalanceSheet = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/accounting/reports/balance-sheet?as_of_date=${asOfDate}`,
        { headers: getAuthHeader() }
      );
      setBalanceSheet(res.data);
    } catch (error) {
      toast.error('Failed to fetch balance sheet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalanceSheet();
  }, [asOfDate]);

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/reports/balance-sheet/pdf?as_of_date=${asOfDate}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `balance_sheet_${asOfDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/reports/balance-sheet/excel?as_of_date=${asOfDate}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `balance_sheet_${asOfDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exported!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Balance Sheet</CardTitle>
          <CardDescription>As of {asOfDate}</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-bs-pdf">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" onClick={handleExportExcel} data-testid="export-bs-excel">
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : balanceSheet ? (
          <div className="grid grid-cols-2 gap-6">
            {/* Assets */}
            <div>
              <h3 className="text-lg font-semibold text-blue-700 mb-3">Assets</h3>
              
              {balanceSheet.assets?.current?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Current Assets</h4>
                  {balanceSheet.assets.current.map((item) => (
                    <div key={item.account_code} className="flex justify-between py-1">
                      <span>{item.account_name}</span>
                      <span>{formatCurrency(item.balance)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {balanceSheet.assets?.bank?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Bank Accounts</h4>
                  {balanceSheet.assets.bank.map((item) => (
                    <div key={item.account_code} className="flex justify-between py-1">
                      <span>{item.account_name}</span>
                      <span>{formatCurrency(item.balance)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {balanceSheet.assets?.fixed?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Fixed Assets</h4>
                  {balanceSheet.assets.fixed.map((item) => (
                    <div key={item.account_code} className="flex justify-between py-1">
                      <span>{item.account_name}</span>
                      <span>{formatCurrency(item.balance)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="border-t pt-2 mt-2 font-bold flex justify-between">
                <span>Total Assets</span>
                <span className="text-blue-700">{formatCurrency(balanceSheet.total_assets)}</span>
              </div>
            </div>

            {/* Liabilities & Equity */}
            <div>
              <h3 className="text-lg font-semibold text-red-700 mb-3">Liabilities & Equity</h3>
              
              {balanceSheet.liabilities?.current?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Current Liabilities</h4>
                  {balanceSheet.liabilities.current.map((item) => (
                    <div key={item.account_code} className="flex justify-between py-1">
                      <span>{item.account_name}</span>
                      <span>{formatCurrency(item.balance)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {balanceSheet.equity?.items?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Equity</h4>
                  {balanceSheet.equity.items.map((item) => (
                    <div key={item.account_code} className="flex justify-between py-1">
                      <span>{item.account_name}</span>
                      <span>{formatCurrency(item.balance)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="border-t pt-2 mt-2 font-bold flex justify-between">
                <span>Total Liabilities & Equity</span>
                <span className="text-red-700">{formatCurrency(balanceSheet.total_liabilities_equity)}</span>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center py-8 text-muted-foreground">No data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default BalanceSheetTab;
